const pass = (): void => void 0;

const defaultFactor = 2;
const defaultRetryDelay = 100;
const defaultRetryMaxCallsCount = Infinity;
const defaultRetryMaxDelay = 15 * 1000;

type RetryFunc<T> = (options?: RetryOptions) => Promise<T>;

export type RetryOptions = {
    factor: number;
    retryMaxDelay: number;
    retryDelay: number;
    getRetryDelay: (retryCount: number) => number;
    retryMaxCallsCount: number;
    onMaxCallsRunOut: (retryCount: number, retryFunc: (options: RetryOptions) => void) => void;
    retryRule: (result: unknown, options?: RetryOptions) => boolean;
    retryCount: number;
    onRetryTimeoutSet: (options: RetryOptions) => void;
};

const retryOr =
    (
        action: (response: unknown) => void,
        options: RetryOptions,
        retryFunc: (options: RetryOptions) => void
    ) =>
    (response: unknown) => {
        if (!options.retryRule(response, options)) {
            action(response);
        } else if (
            options.retryCount <= options.retryMaxCallsCount ||
            !isFinite(options.retryCount)
        ) {
            setTimeout(
                () => retryFunc(options),
                options.getRetryDelay?.(options.retryCount) ?? options.retryDelay
            );
            options.onRetryTimeoutSet(options);
        } else {
            options.onMaxCallsRunOut(options.retryCount, retryFunc);
            action(response);
        }
    };

const incrementOptions = (options: RetryOptions) => ({
    ...options,
    retryDelay: Math.min(options.retryDelay * options.factor, options.retryMaxDelay),
    retryCount: options.retryCount + 1,
});

const getWrappedRetry = <T>(retryFunc: RetryFunc<T>, retryOptions: RetryOptions) => {
    return new Promise<T>((resolve, reject) => {
        const wrappedRetryFunc = (options: RetryOptions) => {
            retryFunc()
                .then(retryOr(resolve as () => T, incrementOptions(options), wrappedRetryFunc))
                .catch(retryOr(reject, incrementOptions(options), wrappedRetryFunc));
        };

        wrappedRetryFunc(retryOptions);
    });
};

const sanitizeOptions = (incomeOptions: Partial<RetryOptions>) => {
    const options: Partial<RetryOptions> = {};
    options.factor = incomeOptions.factor || defaultFactor;
    options.retryMaxDelay = incomeOptions.retryMaxDelay ?? defaultRetryMaxDelay;
    options.retryDelay = Math.min(
        incomeOptions.retryDelay || defaultRetryDelay,
        options.retryMaxDelay
    );
    options.getRetryDelay = incomeOptions.getRetryDelay;
    options.retryMaxCallsCount = incomeOptions.retryMaxCallsCount ?? defaultRetryMaxCallsCount;
    if (!(incomeOptions.retryRule instanceof Function)) {
        throw 'retryRule property expects a Function';
    }
    if (incomeOptions.onMaxCallsRunOut && !(incomeOptions.onMaxCallsRunOut instanceof Function)) {
        throw 'onMaxCallsRunOut property expects a Function';
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    options.onMaxCallsRunOut = incomeOptions.onMaxCallsRunOut ?? pass;
    options.retryRule = incomeOptions.retryRule;
    options.retryCount = 1;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    options.onRetryTimeoutSet = incomeOptions.onRetryTimeoutSet ?? pass;

    return options as RetryOptions;
};

export default <T>(
    retryFunc: RetryFunc<T>,
    retryOptions: Partial<RetryOptions>
): ReturnType<RetryFunc<T>> => {
    const sanitizedOptions = sanitizeOptions(retryOptions);
    return getWrappedRetry<T>(retryFunc, sanitizedOptions);
};
